/**
 * localCacheService.js - 双层缓存服务
 *
 * ══════════════════════════════════════════════════════════════════
 * 📦 文件缓存与上传架构说明（实现核心）
 *
 * 1. 读取优先级（只要命中一层即直接返回，不走远程）：
 *    ① 内存 blobUrl（极快，页面级缓存）
 *    ② IndexedDB Blob 缓存（所有浏览器，秒开）
 *    ③ 本地文件夹缓存（File System Access API，仅 Chrome/Edge，需授权）
 *    ④ 远程下载（R2/OSS/代理，最慢，仅本地无缓存时才走）
 *
 * 2. 写入策略：
 *    - 每次下载/上传新文件时，同时写入 IndexedDB 和本地文件夹（如已授权）
 *    - IndexedDB 永远生效，无需配置
 *    - 本地文件夹为可选增强层，用户配置后自动同步写入
 *
 * 3. 回填机制：
 *    - 命中 IndexedDB 时，后台自动回填本地文件夹（防止误删/换电脑）
 *    - 命中本地文件夹时，后台自动回填 IndexedDB
 *
 * 4. 绝不出现“AI 幻觉”——只要本地缓存命中，绝不走远程！
 *    - 任何 fallback 到远程的情况都必须有详细日志
 *    - 只要有缓存，必须极快返回
 *
 * 5. 关键函数：getLocalUrlOrFetch
 *    - 统一入口，严格按优先级查找，命中即返回
 *    - 只有全部 miss 才走远程
 *    - 远程下载后双写本地
 *
 * 6. 典型调用链：
 *    - 节点/素材加载 → getLocalUrlOrFetch → (blobUrl/IndexedDB/本地文件夹/远程)
 *
 * ⚠️ 修改注意事项：
 *    - 不要更改优先级顺序！
 *    - 不要在本地命中时 fallback 到远程！
 *    - 任何 fallback 必须详细日志，便于排查
 * ══════════════════════════════════════════════════════════════════
 */

// ==================== API 配置 ====================
import { API_BASE_URL } from '../constants';

// Token 存储 key（与 AuthContext / api.js 保持一致）
const TOKEN_KEY = 'cineart_token';
const SESSION_MODE_KEY = 'cineart_session_mode';
function _getAuthToken() {
  if (sessionStorage.getItem(SESSION_MODE_KEY)) {
    return sessionStorage.getItem(TOKEN_KEY);
  }
  return localStorage.getItem(TOKEN_KEY);
}

/**
 * 判断一个 URL 是否与当前站点**同源**（协议 + 主机 + 端口全同）。
 *
 * 为什么要按 origin 比，而不是看「是不是绝对 URL」：
 *   - 相对路径（/thumbnails/x.webp）→ 同源，但 new URL() 解析需要 base
 *   - 绝对 URL（同源，例：https://cineart.dlpu.edu.cn/cineart-media/x.png）→ 同源
 *   - 绝对 URL（跨域，例：https://bucket.oss-cn-x.aliyuncs.com/x.png）→ 跨域
 *
 * 只有跨域的才需要服务端代理绕 CORS；同源的直连本来就没问题，
 * 走代理不仅白等超时，还会让服务器把同一个文件再拉一遍。
 *
 * @param {string} url
 * @returns {boolean} true = 与当前站点同源
 */
function isSameOrigin(url) {
  if (!url) return true;
  const raw = String(url).trim();
  // 相对路径（同源）—— 无法解析成绝对 URL，交给 new URL(base) 兜底
  if (!/^https?:\/\//i.test(raw)) return true;
  try {
    const u = new URL(raw);
    return u.protocol === window.location.protocol
      && u.hostname === window.location.hostname
      && u.port === window.location.port;
  } catch (_) {
    // 解析失败 → 保守当作跨域，走代理更安全
    return false;
  }
}

// ==================== 第一层：IndexedDB Blob 缓存 ====================

const BLOB_DB_NAME = 'CineartBlobCache';
const BLOB_DB_VERSION = 2;
const BLOB_STORE = 'blobs';
const META_STORE = 'meta';

// 缓存上限：默认 2GB，可通过 localStorage 配置
const DEFAULT_CACHE_BYTES = 2 * 1024 * 1024 * 1024;
function getMaxCacheBytes() {
  try {
    const custom = localStorage.getItem('cineart_cache_limit_gb');
    if (custom) {
      const gb = parseFloat(custom);
      if (gb > 0 && gb <= 20) return gb * 1024 * 1024 * 1024;
    }
  } catch (_) {}
  return DEFAULT_CACHE_BYTES;
}
const MAX_CACHE_BYTES = getMaxCacheBytes();

let blobDbPromise = null;

function openBlobDB() {
  if (blobDbPromise) return blobDbPromise;
  blobDbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(BLOB_DB_NAME, BLOB_DB_VERSION);
    request.onerror = () => {
      blobDbPromise = null;
      reject(request.error);
    };
    request.onsuccess = () => resolve(request.result);
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(BLOB_STORE)) {
        db.createObjectStore(BLOB_STORE);
      }
      if (!db.objectStoreNames.contains(META_STORE)) {
        const metaStore = db.createObjectStore(META_STORE);
        metaStore.createIndex('lastAccess', 'lastAccess', { unique: false });
      }
    };
  });
  return blobDbPromise;
}

// ── lastAccess 批量更新（后台，不阻塞读取）──
// 读取 blob 时改用 readonly 事务以支持并行，lastAccess 写入改为 2 秒防抖批量提交，
// 避免 readwrite 事务串行化导致批量加载变慢（切换图板时通常一次读取 10+ 个 blob）
const pendingLastAccessKeys = new Set();
let lastAccessFlushTimer = null;

async function flushLastAccessUpdates() {
  if (pendingLastAccessKeys.size === 0) return;
  const keys = [...pendingLastAccessKeys];
  pendingLastAccessKeys.clear();
  try {
    const db = await openBlobDB();
    const tx = db.transaction([META_STORE], 'readwrite');
    const metaStore = tx.objectStore(META_STORE);
    const now = Date.now();
    for (const key of keys) {
      const req = metaStore.get(key);
      req.onsuccess = () => {
        const meta = req.result;
        if (meta) { meta.lastAccess = now; metaStore.put(meta, key); }
      };
    }
  } catch (e) { console.warn('批量更新 lastAccess 失败:', e); }
}

function scheduleLastAccessUpdate(key) {
  pendingLastAccessKeys.add(key);
  if (lastAccessFlushTimer) clearTimeout(lastAccessFlushTimer);
  lastAccessFlushTimer = setTimeout(flushLastAccessUpdates, 2000);
}

async function getBlobFromDB(key) {
  const db = await openBlobDB();
  return new Promise((resolve, reject) => {
    // ⚡ readonly 事务：允许多个并行执行，大幅提升批量查询速度
    const tx = db.transaction([BLOB_STORE], 'readonly');
    const blobStore = tx.objectStore(BLOB_STORE);
    const request = blobStore.get(key);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => {
      const blob = request.result;
      if (blob) scheduleLastAccessUpdate(key); // 后台批量更新 lastAccess
      resolve(blob || null);
    };
  });
}

async function putBlobToDB(key, blob, fileType) {
  const db = await openBlobDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction([BLOB_STORE, META_STORE], 'readwrite');
    const blobStore = tx.objectStore(BLOB_STORE);
    const metaStore = tx.objectStore(META_STORE);
    blobStore.put(blob, key);
    metaStore.put({
      size: blob.size,
      lastAccess: Date.now(),
      createdAt: Date.now(),
      fileType: fileType || 'image',
      userId: currentUserId
    }, key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

async function deleteBlobFromDB(key) {
  const db = await openBlobDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction([BLOB_STORE, META_STORE], 'readwrite');
    tx.objectStore(BLOB_STORE).delete(key);
    tx.objectStore(META_STORE).delete(key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

async function getCacheStats() {
  const db = await openBlobDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(META_STORE, 'readonly');
    const store = tx.objectStore(META_STORE);
    const request = store.openCursor();
    let totalSize = 0;
    const entries = [];
    request.onsuccess = (e) => {
      const cursor = e.target.result;
      if (cursor) {
        const meta = cursor.value;
        totalSize += meta.size || 0;
        entries.push({ key: cursor.key, ...meta });
        cursor.continue();
      } else {
        resolve({ totalSize, entries });
      }
    };
    request.onerror = () => reject(request.error);
  });
}

async function evictIfNeeded(incomingBytes = 0) {
  try {
    const { totalSize, entries } = await getCacheStats();
    if (totalSize + incomingBytes <= MAX_CACHE_BYTES) return;
    entries.sort((a, b) => a.lastAccess - b.lastAccess);
    let freed = 0;
    const target = totalSize + incomingBytes - MAX_CACHE_BYTES;
    for (const entry of entries) {
      if (freed >= target) break;
      await deleteBlobFromDB(entry.key);
      freed += entry.size || 0;
    }
    console.log('🧹 缓存淘汰完成，释放 ' + (freed / 1024 / 1024).toFixed(1) + 'MB');
  } catch (e) {
    console.warn('缓存淘汰失败:', e);
  }
}

// ==================== 第二层：File System Access 本地文件夹 ====================

const FS_DB_NAME = 'CineartCacheDB';
const FS_DB_VERSION = 1;
const FS_STORE_NAME = 'cacheConfig';
const BG_REMOVAL_RUNTIME_CACHE = 'cineart-bg-removal-v1';
const BG_REMOVAL_SYSTEM_FOLDER = '_system';
const BG_REMOVAL_FOLDER = 'bg-removal';
const FFMPEG_RUNTIME_CACHE = 'cineart-ffmpeg-core-v1';
const FFMPEG_SYSTEM_FOLDER = '_system';
const FFMPEG_FOLDER = 'ffmpeg-core';
const FFMPEG_ASSET_FILE_NAMES = ['ffmpeg-core.js', 'ffmpeg-core.wasm'];

// 当前登录用户的 ID（用于区分不同用户的缓存配置）
// ⚠️ 注意：这是模块单例变量。AuthContext 的 logout 必须先调用 setCurrentUser(null)
//    清理缓存，login 成功后立即调用 setCurrentUser(user.id) 设置新用户，
//    否则用户切换间隙可能导致缓存 key 混用。
let currentUserId = null;

// 获取当前用户的 key 前缀
function getUserKey(baseKey) {
  if (!currentUserId) {
    // 未登录状态不应该访问缓存，返回隔离 key 防止混用（静默处理，不污染控制台）
    return `__anonymous__${baseKey}`;
  }
  return `user_${currentUserId}_${baseKey}`;
}

// 内存中缓存的 handle
let cachedHandle = null;
let cachedPath = null;

function openFsDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(FS_DB_NAME, FS_DB_VERSION);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(FS_STORE_NAME)) {
        db.createObjectStore(FS_STORE_NAME);
      }
    };
  });
}

async function getFromFsDB(key) {
  const db = await openFsDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(FS_STORE_NAME, 'readonly');
    const store = tx.objectStore(FS_STORE_NAME);
    const request = store.get(key);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
  });
}

async function setToFsDB(key, value) {
  const db = await openFsDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(FS_STORE_NAME, 'readwrite');
    const store = tx.objectStore(FS_STORE_NAME);
    const request = store.put(value, key);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve();
  });
}

async function deleteFromFsDB(key) {
  const db = await openFsDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(FS_STORE_NAME, 'readwrite');
    const store = tx.objectStore(FS_STORE_NAME);
    const request = store.delete(key);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve();
  });
}

// ==================== 内存 Blob URL 缓存（LRU 淘汰） ====================
const MAX_BLOB_CACHE_SIZE = 200;  // 超过此数量时淘汰最旧的条目
const blobUrlCache = new Map();   // Map 保持插入顺序，天然支持 LRU

// 🔒 blob URL 保活引用计数：cacheKey → 引用数
//   下游参考图缩略图长期挂着某个 blob URL，若被 LRU 淘汰或 60s 定时 revoke，
//   <img src=blob:…> 会失效 → 回退到 OSS 原图重新全尺寸下载（"刷半天"的根因之一）。
//   引用计数 > 0 的 blob 不参与 LRU 淘汰、也不会被 deferredRevoke 释放。
const blobUrlRefCount = new Map();  // cacheKey → number

/**
 * 🔒 增加某个 blob URL 的保活引用（按 blob URL 反查 cacheKey）。
 * 供 onConnect 等场景调用：把上游图片继承给下游参考区时，钉住这个 blob，
 * 防止它在缩略图仍在显示期间被回收。找不到对应 cacheKey 时静默忽略。
 */
export function pinBlobUrl(blobUrl) {
  if (!blobUrl || !blobUrl.startsWith('blob:')) return;
  for (const [key, url] of blobUrlCache.entries()) {
    if (url === blobUrl) {
      blobUrlRefCount.set(key, (blobUrlRefCount.get(key) || 0) + 1);
      return;
    }
  }
}

/**
 * 🔓 释放一个保活引用。计数归零时移除记录，blob 恢复可被 LRU/revoke 回收。
 */
export function unpinBlobUrl(blobUrl) {
  if (!blobUrl || !blobUrl.startsWith('blob:')) return;
  for (const [key, url] of blobUrlCache.entries()) {
    if (url === blobUrl) {
      const n = (blobUrlRefCount.get(key) || 0) - 1;
      if (n <= 0) blobUrlRefCount.delete(key);
      else blobUrlRefCount.set(key, n);
      return;
    }
  }
}

/**
 * 延迟 revoke blob URL（30 秒后释放）
 * 立即 revoke 会导致仍在渲染的 <img>/<video> 白屏，
 * 延迟 30 秒给 React 足够时间切换到新 URL 或卸载组件。
 */
// C3: 缩短延迟回收时间，避免长会话中数百个 blob 同时占用内存
// 60 秒足够 React 完成 UI 切换（含全屏预览等延迟操作），之后安全释放
function deferredRevoke(blobUrl) {
  setTimeout(() => URL.revokeObjectURL(blobUrl), 60 * 1000);
}

function createCachedBlobUrl(cacheKey, blob) {
  // 同一 key 已有有效 blob URL 则直接复用，避免 revoke 导致正在使用的 URL 失效
  if (blobUrlCache.has(cacheKey)) {
    // 访问时移到末尾（LRU 更新）
    const existing = blobUrlCache.get(cacheKey);
    blobUrlCache.delete(cacheKey);
    blobUrlCache.set(cacheKey, existing);
    return existing;
  }
  // 淘汰最旧的条目 → 延迟 30 秒 revoke（给正在使用的 <img>/<video> 缓冲时间）
  // 🔒 跳过被钉住（引用计数 > 0）的条目，从最旧向后找第一个未钉住的淘汰。
  if (blobUrlCache.size >= MAX_BLOB_CACHE_SIZE) {
    let evictKey = null;
    for (const key of blobUrlCache.keys()) {
      if (!blobUrlRefCount.has(key)) { evictKey = key; break; }
    }
    if (evictKey) {
      const oldUrl = blobUrlCache.get(evictKey);
      blobUrlCache.delete(evictKey);
      deferredRevoke(oldUrl);
    }
    // 全部被钉住时不淘汰（缓存临时超出上限，随引用释放后恢复）
  }
  const blobUrl = URL.createObjectURL(blob);
  blobUrlCache.set(cacheKey, blobUrl);
  return blobUrl;
}

export function clearBlobUrlCache() {
  for (const url of blobUrlCache.values()) {
    URL.revokeObjectURL(url);
  }
  blobUrlCache.clear();
  blobUrlRefCount.clear();
}

// ==================== 下载进度追踪 ====================
//
// 架构说明：
//   下载发生在本模块的 getLocalUrlOrFetch() 中（由 EditorPage 批量调用），
//   但进度需要在 ImageNodeView / VideoNodeView 中显示。
//   使用 Map 作为共享进度存储，节点视图每 500ms 轮询读取，
//   避免 React state 透传导致无关节点重渲染。
//
// 数据流：
//   fetchBlobWithProgress() → 写入 downloadProgressStore
//   节点视图 useEffect(setInterval) → getDownloadProgressByUrl() → 读取
//
// 依赖：服务端 /api/proxy-image 需转发 Content-Length + Expose-Headers
//       否则通过代理下载时 total 为 0，只能显示已下载大小
//
// Key: cacheKey ("file://xxx.png"), Value: { downloaded, total, status }
const downloadProgressStore = new Map();

/**
 * 通过媒体 URL 查询下载进度
 * @param {string} url - 媒体文件的 OSS/R2 URL
 * @returns {{ downloaded: number, total: number, status: 'downloading'|'done'|'error' } | null}
 */
export function getDownloadProgressByUrl(url) {
  if (!url) return null;
  const fileName = extractFileNameFromUrl(url);
  if (!fileName) return null;
  return downloadProgressStore.get('file://' + fileName) || null;
}

/**
 * 使用 ReadableStream 下载并实时追踪进度
 * @param {Response} response - fetch Response 对象
 * @param {string} cacheKey - 进度追踪键（如 "file://abc.png"）
 * @returns {Promise<Blob>}
 */
async function fetchBlobWithProgress(response, cacheKey) {
  const contentLength = parseInt(response.headers.get('Content-Length') || '0', 10);

  // 浏览器不支持 ReadableStream 时降级
  if (!response.body || typeof response.body.getReader !== 'function') {
    downloadProgressStore.set(cacheKey, { downloaded: 0, total: contentLength, status: 'downloading' });
    const blob = await response.blob();
    // 🛡️ 护栏 1：拒绝 0 字节响应（CDN 边缘节点抽风 / 厂商 URL 已过期但返回 200 空 body）
    if (blob.size === 0) {
      throw new Error('远程响应为 0 字节（疑似厂商 URL 已过期或 CDN 异常）');
    }
    downloadProgressStore.set(cacheKey, { downloaded: blob.size, total: blob.size, status: 'downloading' });
    return blob;
  }

  const reader = response.body.getReader();
  const chunks = [];
  let downloaded = 0;
  downloadProgressStore.set(cacheKey, { downloaded: 0, total: contentLength, status: 'downloading' });

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    downloaded += value.length;
    downloadProgressStore.set(cacheKey, { downloaded, total: contentLength, status: 'downloading' });
  }

  const contentType = response.headers.get('Content-Type') || 'application/octet-stream';
  const blob = new Blob(chunks, { type: contentType });

  // 🛡️ 护栏 1：完整性校验 —— 防止流被打断 / CDN 空响应进入缓存
  // 失败抛错，让 getLocalUrlOrFetch 走代理 fallback 重试，
  // 而不是把脏数据写进 IndexedDB 造成永久污染。
  if (blob.size === 0) {
    throw new Error('下载完成但 blob 为 0 字节（流被中断或厂商 URL 失效）');
  }
  if (contentLength > 0 && blob.size !== contentLength) {
    throw new Error(`下载不完整：期望 ${contentLength} 字节，实际收到 ${blob.size} 字节`);
  }

  return blob;
}

/**
 * 🛡️ C4: 清理 IndexedDB Blob 缓存（用户切换时调用）
 * 清空 blobs 和 meta 两个 object store，防止跨用户数据残留
 */
async function clearIndexedDBCache() {
  try {
    const db = await openBlobDB();
    const tx = db.transaction([BLOB_STORE, META_STORE], 'readwrite');
    tx.objectStore(BLOB_STORE).clear();
    tx.objectStore(META_STORE).clear();
    await new Promise((resolve, reject) => {
      tx.oncomplete = resolve;
      tx.onerror = () => reject(tx.error);
    });
    console.log('🧹 已清理 IndexedDB Blob 缓存');
  } catch (err) {
    console.warn('清理 IndexedDB 缓存失败:', err);
  }
}

// ==================== 文件系统操作（原版完整恢复） ====================

/**
 * 检查浏览器是否支持 File System Access API
 */
export function isFileSystemAccessSupported() {
  return 'showDirectoryPicker' in window;
}

/**
 * 设置当前用户 ID（登录时调用）
 * 切换用户时清理所有缓存层：内存 blob URL + IndexedDB Blob 缓存
 */
export function setCurrentUser(userId) {
  if (currentUserId !== userId) {
    cachedHandle = null;
    cachedPath = null;
    clearBlobUrlCache();
    // 🛡️ C4: 切换用户时清理 IndexedDB 缓存，防止跨用户数据残留
    if (currentUserId !== null && userId !== null && currentUserId !== userId) {
      clearIndexedDBCache().catch(err => console.warn('清理 IndexedDB 缓存失败:', err));
    }
    currentUserId = userId;
  }
}

/**
 * 获取当前用户 ID
 */
export function getCurrentUserId() {
  return currentUserId;
}

/**
 * 检查是否已配置本地缓存
 */
export async function hasCacheConfigured() {
  if (cachedHandle) return true;
  try {
    const handle = await getFromFsDB(getUserKey('rootFolderHandle'));
    return !!handle;
  } catch (e) {
    console.warn('检查缓存配置失败:', e);
    return false;
  }
}

/**
 * 获取当前缓存路径（显示用）
 */
export async function getCachePath() {
  if (cachedPath) return cachedPath;
  try {
    const path = await getFromFsDB(getUserKey('rootFolderPath'));
    return path || null;
  } catch (e) {
    return null;
  }
}

/**
 * 请求用户选择缓存文件夹
 */
export async function requestCacheFolder(customPath) {
  if (!isFileSystemAccessSupported()) {
    return {
      success: false,
      error: '您的浏览器不支持本地缓存功能，请使用 Chrome 或 Edge 浏览器'
    };
  }

  try {
    const handle = await window.showDirectoryPicker({
      mode: 'readwrite',
      startIn: 'documents'
    });

    const displayPath = customPath || handle.name;

    await setToFsDB(getUserKey('rootFolderHandle'), handle);
    await setToFsDB(getUserKey('rootFolderPath'), displayPath);

    cachedHandle = handle;
    cachedPath = displayPath;

    console.log('✅ 本地缓存文件夹已配置:', displayPath);

    // 🆕 派发全局事件，通知所有组件缓存配置已变更
    window.dispatchEvent(new CustomEvent('cache-config-changed', {
      detail: { path: displayPath, valid: true }
    }));

    return { success: true, path: displayPath, handle };
  } catch (e) {
    if (e.name === 'AbortError') {
      return { success: false, error: '已取消选择' };
    }
    console.error('选择文件夹失败:', e);
    return { success: false, error: e.message || '选择文件夹失败' };
  }
}

/**
 * 更新缓存路径显示（不重新选择文件夹）
 */
export async function updateCachePath(newPath) {
  if (!newPath) return { success: false, error: '路径不能为空' };
  try {
    await setToFsDB(getUserKey('rootFolderPath'), newPath);
    cachedPath = newPath;
    return { success: true, path: newPath };
  } catch (e) {
    return { success: false, error: e.message };
  }
}

/**
 * 从 IndexedDB 恢复 handle 并请求权限
 */
export async function restoreCacheFolder() {
  if (!isFileSystemAccessSupported()) {
    return { success: false, error: '浏览器不支持' };
  }

  if (cachedHandle) {
    try {
      const permission = await cachedHandle.queryPermission({ mode: 'readwrite' });
      if (permission === 'granted') {
        return { success: true, path: cachedPath };
      }
    } catch (e) {
      // handle 可能失效，继续尝试从 IndexedDB 恢复
    }
  }

  try {
    const handle = await getFromFsDB(getUserKey('rootFolderHandle'));
    const path = await getFromFsDB(getUserKey('rootFolderPath'));

    if (!handle) {
      return { success: false, needReselect: true };
    }

    const permission = await handle.queryPermission({ mode: 'readwrite' });

    if (permission === 'granted') {
      cachedHandle = handle;
      cachedPath = path;
      return { success: true, path };
    }

    // 权限未授予（prompt/denied），不尝试 requestPermission（需要用户手势）
    // 静默返回失败，让调用方降级到其他缓存层
    console.debug('本地文件夹权限未就绪，跳过本地缓存层');
    return { success: false, needReauth: true };
  } catch (e) {
    console.debug('恢复缓存文件夹失败:', e.message);
    return { success: false, error: e.message, needReselect: true };
  }
}

/**
 * 一键重新授权本地缓存文件夹（必须在用户点击事件中调用）
 * 用于权限过期后，用户点击"恢复缓存"按钮时调用
 */
export async function reauthorizeCacheFolder() {
  if (!isFileSystemAccessSupported()) {
    return { success: false, error: '浏览器不支持' };
  }

  try {
    const handle = await getFromFsDB(getUserKey('rootFolderHandle'));
    const path = await getFromFsDB(getUserKey('rootFolderPath'));

    if (!handle) {
      return { success: false, needReselect: true };
    }

    const result = await handle.requestPermission({ mode: 'readwrite' });
    if (result === 'granted') {
      cachedHandle = handle;
      cachedPath = path;
      console.log('✅ 本地缓存权限已恢复:', path);
      window.dispatchEvent(new CustomEvent('cache-config-changed', {
        detail: { path, valid: true }
      }));
      return { success: true, path };
    }

    return { success: false, error: '权限被拒绝' };
  } catch (e) {
    console.error('重新授权失败:', e);
    return { success: false, error: e.message };
  }
}

/**
 * 清除缓存配置
 */
export async function clearCacheConfig() {
  try {
    await deleteFromFsDB(getUserKey('rootFolderHandle'));
    await deleteFromFsDB(getUserKey('rootFolderPath'));
    cachedHandle = null;
    cachedPath = null;
    console.log('🗑️ 本地缓存配置已清除');
    return { success: true };
  } catch (e) {
    console.error('清除缓存配置失败:', e);
    return { success: false, error: e.message };
  }
}

/**
 * 获取或创建子文件夹
 */
async function getOrCreateSubfolder(parentHandle, name) {
  try {
    return await parentHandle.getDirectoryHandle(name, { create: true });
  } catch (e) {
    console.error('创建文件夹 ' + name + ' 失败:', e);
    throw e;
  }
}

function normalizeBgRemovalBaseUrl(baseUrl) {
  if (!baseUrl) return `${window.location.origin}/bg-removal/`;
  return baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;
}

function getBgRemovalAssetContentType(fileName) {
  if (!fileName) return 'application/octet-stream';
  const lower = fileName.toLowerCase();
  if (lower.endsWith('.json')) return 'application/json';
  if (lower.endsWith('.wasm')) return 'application/wasm';
  return 'application/octet-stream';
}

function collectBgRemovalChunkNames(resourcesJson) {
  const names = new Set();
  if (!resourcesJson || typeof resourcesJson !== 'object') return [];
  Object.values(resourcesJson).forEach((entry) => {
    if (!entry || !Array.isArray(entry.chunks)) return;
    entry.chunks.forEach((chunk) => {
      if (chunk && chunk.name) names.add(chunk.name);
    });
  });
  return [...names];
}

async function getBgRemovalFolder(create = false) {
  if (!cachedHandle) {
    const restored = await restoreCacheFolder();
    if (!restored.success) return null;
  }

  try {
    const systemFolder = create
      ? await getOrCreateSubfolder(cachedHandle, BG_REMOVAL_SYSTEM_FOLDER)
      : await cachedHandle.getDirectoryHandle(BG_REMOVAL_SYSTEM_FOLDER);
    return create
      ? await getOrCreateSubfolder(systemFolder, BG_REMOVAL_FOLDER)
      : await systemFolder.getDirectoryHandle(BG_REMOVAL_FOLDER);
  } catch (e) {
    if (!create && e.name === 'NotFoundError') return null;
    throw e;
  }
}

async function writeBgRemovalAssetToLocal(fileName, blob) {
  if (!fileName || !blob) return false;
  try {
    const folder = await getBgRemovalFolder(true);
    if (!folder) return false;
    const fileHandle = await folder.getFileHandle(fileName, { create: true });
    const writable = await fileHandle.createWritable();
    await writable.write(blob);
    await writable.close();
    return true;
  } catch (e) {
    if (e.name !== 'NotAllowedError' && e.name !== 'SecurityError') {
      console.warn('写入抠图模型本地缓存失败:', e.message);
    }
    return false;
  }
}

async function readBgRemovalAssetFromLocal(fileName) {
  if (!fileName) return null;
  try {
    const folder = await getBgRemovalFolder(false);
    if (!folder) return null;
    const fileHandle = await folder.getFileHandle(fileName);
    const file = await fileHandle.getFile();
    return file;
  } catch (e) {
    return null;
  }
}

async function readBgRemovalResourcesFromBrowserCache(cache, baseUrl) {
  const normalizedBase = normalizeBgRemovalBaseUrl(baseUrl);
  const resourcesUrl = `${normalizedBase}resources.json`;
  const response = await cache.match(resourcesUrl);
  if (!response) return null;
  try {
    const json = await response.clone().json();
    return { json, response };
  } catch (e) {
    return null;
  }
}

/**
 * 把本地文件夹中的抠图模型资源回填到浏览器 CacheStorage。
 * 用于浏览器缓存被清空后的快速恢复。
 */
export async function restoreBgRemovalAssetsToBrowserCache(baseUrl) {
  if (typeof window === 'undefined' || !('caches' in window)) {
    return { success: false, reason: 'cache-storage-unsupported' };
  }

  const normalizedBase = normalizeBgRemovalBaseUrl(baseUrl);
  const cache = await caches.open(BG_REMOVAL_RUNTIME_CACHE);

  const resourcesBlob = await readBgRemovalAssetFromLocal('resources.json');
  if (!resourcesBlob) {
    return { success: false, reason: 'local-resources-miss', restored: 0 };
  }

  await cache.put(
    `${normalizedBase}resources.json`,
    new Response(resourcesBlob, {
      headers: { 'Content-Type': 'application/json' }
    })
  );

  let resourcesJson = null;
  try {
    resourcesJson = JSON.parse(await resourcesBlob.text());
  } catch (e) {
    console.warn('本地 resources.json 解析失败，跳过抠图缓存回填');
    return { success: false, reason: 'local-resources-invalid', restored: 1 };
  }

  const chunkNames = collectBgRemovalChunkNames(resourcesJson);
  let restored = 1;
  for (const fileName of chunkNames) {
    const blob = await readBgRemovalAssetFromLocal(fileName);
    if (!blob) continue;
    await cache.put(
      `${normalizedBase}${fileName}`,
      new Response(blob, {
        headers: { 'Content-Type': getBgRemovalAssetContentType(fileName) }
      })
    );
    restored += 1;
  }

  return { success: true, restored, total: chunkNames.length + 1 };
}

/**
 * 把浏览器 CacheStorage 中的抠图模型资源回填到本地缓存文件夹。
 * 用于首次下载后落盘，避免下次因浏览器缓存清空而重新下载。
 */
export async function syncBgRemovalAssetsFromBrowserCache(baseUrl) {
  if (typeof window === 'undefined' || !('caches' in window)) {
    return { success: false, reason: 'cache-storage-unsupported' };
  }

  const normalizedBase = normalizeBgRemovalBaseUrl(baseUrl);
  const cache = await caches.open(BG_REMOVAL_RUNTIME_CACHE);
  const resourcesResult = await readBgRemovalResourcesFromBrowserCache(cache, normalizedBase);

  if (!resourcesResult) {
    return { success: false, reason: 'browser-resources-miss', synced: 0 };
  }

  const resourcesBlob = await resourcesResult.response.clone().blob();
  await writeBgRemovalAssetToLocal('resources.json', resourcesBlob);

  const chunkNames = collectBgRemovalChunkNames(resourcesResult.json);
  let synced = 1;
  for (const fileName of chunkNames) {
    const response = await cache.match(`${normalizedBase}${fileName}`);
    if (!response) continue;
    const blob = await response.clone().blob();
    const ok = await writeBgRemovalAssetToLocal(fileName, blob);
    if (ok) synced += 1;
  }

  return { success: true, synced, total: chunkNames.length + 1 };
}

/**
 * 抠图前预热：优先从本地文件夹恢复到浏览器缓存；若资源缺失，再从站点拉取 resources.json。
 */
export async function prepareBgRemovalAssets(baseUrl) {
  if (typeof window === 'undefined' || !('caches' in window)) {
    return { success: false, reason: 'cache-storage-unsupported' };
  }

  const normalizedBase = normalizeBgRemovalBaseUrl(baseUrl);
  const restored = await restoreBgRemovalAssetsToBrowserCache(normalizedBase).catch(() => ({ success: false }));
  const cache = await caches.open(BG_REMOVAL_RUNTIME_CACHE);
  const resourcesUrl = `${normalizedBase}resources.json`;
  const existing = await cache.match(resourcesUrl);

  if (!existing) {
    try {
      const response = await fetch(resourcesUrl);
      if (response.ok) {
        await cache.put(resourcesUrl, response.clone());
        const blob = await response.blob();
        await writeBgRemovalAssetToLocal('resources.json', blob);
      }
    } catch (e) {
      // 站点资源不可用时交给 worker 内的 CDN fallback。
    }
  }

  return {
    success: true,
    restoredFromLocal: restored?.restored || 0
  };
}

/**
 * 在空闲时把整套抠图资源静默下载到浏览器缓存和本地文件夹。
 * 与 prepareBgRemovalAssets 的区别：它不只准备 resources.json，而是补齐全部 chunk。
 */
export async function warmBgRemovalAssets(baseUrl) {
  if (typeof window === 'undefined' || !('caches' in window)) {
    return { success: false, reason: 'cache-storage-unsupported' };
  }

  const normalizedBase = normalizeBgRemovalBaseUrl(baseUrl);
  const cache = await caches.open(BG_REMOVAL_RUNTIME_CACHE);

  await restoreBgRemovalAssetsToBrowserCache(normalizedBase).catch(() => ({ success: false }));

  let resourcesResult = await readBgRemovalResourcesFromBrowserCache(cache, normalizedBase);
  if (!resourcesResult) {
    try {
      const response = await fetch(`${normalizedBase}resources.json`);
      if (!response.ok) {
        return { success: false, reason: 'resources-fetch-failed', warmed: 0 };
      }
      await cache.put(`${normalizedBase}resources.json`, response.clone());
      const resourcesBlob = await response.clone().blob();
      await writeBgRemovalAssetToLocal('resources.json', resourcesBlob);
      const json = JSON.parse(await resourcesBlob.text());
      resourcesResult = {
        json,
        response: new Response(resourcesBlob, {
          headers: { 'Content-Type': 'application/json' }
        })
      };
    } catch (e) {
      return { success: false, reason: 'resources-fetch-failed', error: e.message, warmed: 0 };
    }
  }

  const chunkNames = collectBgRemovalChunkNames(resourcesResult.json);
  let cached = 1;
  let downloaded = 0;

  for (const fileName of chunkNames) {
    const assetUrl = `${normalizedBase}${fileName}`;
    const existing = await cache.match(assetUrl);
    if (existing) {
      cached += 1;
      continue;
    }

    try {
      const response = await fetch(assetUrl);
      if (!response.ok) continue;
      await cache.put(assetUrl, response.clone());
      const blob = await response.clone().blob();
      await writeBgRemovalAssetToLocal(fileName, blob);
      cached += 1;
      downloaded += 1;
    } catch (_) {
      // 忽略单文件失败，保留首次使用时的 worker/CDN 回退链路。
    }
  }

  await syncBgRemovalAssetsFromBrowserCache(normalizedBase).catch(() => {});

  return {
    success: cached > 1,
    cached,
    downloaded,
    total: chunkNames.length + 1
  };
}

function normalizeFfmpegBaseUrl(baseUrl) {
  if (!baseUrl) return `${window.location.origin}/ffmpeg-core/`;
  return baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;
}

function getFfmpegAssetContentType(fileName) {
  if (fileName === 'ffmpeg-core.js') return 'text/javascript';
  if (fileName === 'ffmpeg-core.wasm') return 'application/wasm';
  return 'application/octet-stream';
}

async function getFfmpegFolder(create = false) {
  if (!cachedHandle) {
    const restored = await restoreCacheFolder();
    if (!restored.success) return null;
  }

  try {
    const systemFolder = create
      ? await getOrCreateSubfolder(cachedHandle, FFMPEG_SYSTEM_FOLDER)
      : await cachedHandle.getDirectoryHandle(FFMPEG_SYSTEM_FOLDER);
    return create
      ? await getOrCreateSubfolder(systemFolder, FFMPEG_FOLDER)
      : await systemFolder.getDirectoryHandle(FFMPEG_FOLDER);
  } catch (e) {
    if (!create && e.name === 'NotFoundError') return null;
    throw e;
  }
}

async function writeFfmpegAssetToLocal(fileName, blob) {
  if (!fileName || !blob) return false;
  try {
    const folder = await getFfmpegFolder(true);
    if (!folder) return false;
    const fileHandle = await folder.getFileHandle(fileName, { create: true });
    const writable = await fileHandle.createWritable();
    await writable.write(blob);
    await writable.close();
    return true;
  } catch (e) {
    if (e.name !== 'NotAllowedError' && e.name !== 'SecurityError') {
      console.warn('写入 FFmpeg 本地缓存失败:', e.message);
    }
    return false;
  }
}

async function readFfmpegAssetFromLocal(fileName) {
  if (!fileName) return null;
  try {
    const folder = await getFfmpegFolder(false);
    if (!folder) return null;
    const fileHandle = await folder.getFileHandle(fileName);
    return await fileHandle.getFile();
  } catch (e) {
    return null;
  }
}

/**
 * 把本地文件夹中的 FFmpeg 核心资源回填到浏览器 CacheStorage。
 */
export async function restoreFfmpegAssetsToBrowserCache(baseUrl) {
  if (typeof window === 'undefined' || !('caches' in window)) {
    return { success: false, reason: 'cache-storage-unsupported' };
  }

  const normalizedBase = normalizeFfmpegBaseUrl(baseUrl);
  const cache = await caches.open(FFMPEG_RUNTIME_CACHE);

  let restored = 0;
  for (const fileName of FFMPEG_ASSET_FILE_NAMES) {
    const blob = await readFfmpegAssetFromLocal(fileName);
    if (!blob) continue;
    await cache.put(
      `${normalizedBase}${fileName}`,
      new Response(blob, {
        headers: { 'Content-Type': getFfmpegAssetContentType(fileName) }
      })
    );
    restored += 1;
  }

  if (restored === 0) {
    return { success: false, reason: 'local-assets-miss', restored: 0, total: FFMPEG_ASSET_FILE_NAMES.length };
  }

  return { success: true, restored, total: FFMPEG_ASSET_FILE_NAMES.length };
}

/**
 * 把浏览器 CacheStorage 中的 FFmpeg 核心资源回填到本地缓存文件夹。
 */
export async function syncFfmpegAssetsFromBrowserCache(baseUrl) {
  if (typeof window === 'undefined' || !('caches' in window)) {
    return { success: false, reason: 'cache-storage-unsupported' };
  }

  const normalizedBase = normalizeFfmpegBaseUrl(baseUrl);
  const cache = await caches.open(FFMPEG_RUNTIME_CACHE);

  let synced = 0;
  for (const fileName of FFMPEG_ASSET_FILE_NAMES) {
    const response = await cache.match(`${normalizedBase}${fileName}`);
    if (!response) continue;
    const blob = await response.clone().blob();
    const ok = await writeFfmpegAssetToLocal(fileName, blob);
    if (ok) synced += 1;
  }

  if (synced === 0) {
    return { success: false, reason: 'browser-assets-miss', synced: 0, total: FFMPEG_ASSET_FILE_NAMES.length };
  }

  return { success: true, synced, total: FFMPEG_ASSET_FILE_NAMES.length };
}

/**
 * 从任意远端地址拉取 FFmpeg 资源，并写入浏览器缓存 + 本地文件夹。
 */
export async function cacheFfmpegAssetsFromRemote(remoteBaseUrl, targetBaseUrl) {
  if (typeof window === 'undefined' || !('caches' in window)) {
    return { success: false, reason: 'cache-storage-unsupported' };
  }

  const remoteBase = normalizeFfmpegBaseUrl(remoteBaseUrl);
  const targetBase = normalizeFfmpegBaseUrl(targetBaseUrl || remoteBaseUrl);
  const cache = await caches.open(FFMPEG_RUNTIME_CACHE);

  let cached = 0;
  for (const fileName of FFMPEG_ASSET_FILE_NAMES) {
    try {
      const response = await fetch(`${remoteBase}${fileName}`);
      if (!response.ok) continue;
      await cache.put(`${targetBase}${fileName}`, response.clone());
      const blob = await response.clone().blob();
      await writeFfmpegAssetToLocal(fileName, blob);
      cached += 1;
    } catch (_) {
      // 忽略单文件失败，继续处理其他资源。
    }
  }

  if (cached === 0) {
    return { success: false, reason: 'remote-fetch-failed', cached: 0, total: FFMPEG_ASSET_FILE_NAMES.length };
  }

  return { success: true, cached, total: FFMPEG_ASSET_FILE_NAMES.length };
}

/**
 * FFmpeg 预热：先从本地文件夹恢复浏览器缓存；缓存缺失时再从站点地址拉取并回写。
 */
export async function prepareFfmpegAssets(baseUrl) {
  if (typeof window === 'undefined' || !('caches' in window)) {
    return { success: false, reason: 'cache-storage-unsupported' };
  }

  const normalizedBase = normalizeFfmpegBaseUrl(baseUrl);
  const restored = await restoreFfmpegAssetsToBrowserCache(normalizedBase).catch(() => ({ success: false, restored: 0 }));
  const cache = await caches.open(FFMPEG_RUNTIME_CACHE);

  let hit = 0;
  for (const fileName of FFMPEG_ASSET_FILE_NAMES) {
    const existing = await cache.match(`${normalizedBase}${fileName}`);
    if (existing) hit += 1;
  }

  if (hit < FFMPEG_ASSET_FILE_NAMES.length) {
    await cacheFfmpegAssetsFromRemote(normalizedBase, normalizedBase).catch(() => {});
  }

  return {
    success: true,
    restoredFromLocal: restored?.restored || 0
  };
}

// ==================== 公共工具函数 ====================

/**
 * 🚀 快速本地缓存查找（L0 内存 + L1 IndexedDB + L2 本地文件夹）
 *
 * 与 getLocalUrlOrFetch 的区别：
 *   - 绝不访问网络（L3 远程下载）
 *   - L2 本地文件夹仅在用户已授权（cachedHandle 已存在）时尝试，
 *     不会触发新的权限弹窗 / 阻塞首屏渲染
 *   - 用于项目加载时的「先缓存后渲染」策略：
 *     先用此函数批量解析已有缓存 → 带着 localCacheUrl 首次渲染 → 后台再处理 cache miss
 *   - 典型延迟：L0 命中 ~0ms / L1 命中 ~5-50ms / L2 命中 ~50-200ms
 *
 * 修复历史：
 *   2026-06-06 之前只查 L0+L1，导致 IndexedDB 被淘汰但 L2 还有的节点
 *   首屏先以 OSS URL shimmer，第二阶段才补 localCacheUrl，UI 闪两次。
 *   现把 L2 也并入快速查找路径，配过本地文件夹的用户首屏即满图。
 *
 * @param {string} url  OSS URL 或 local-cache:// URL
 * @param {object} [opts]  可选参数；提供后启用 L2 查找
 * @param {string} [opts.crewId]      项目 ID
 * @param {string} [opts.crewName]    项目名（用于路径定位）
 * @param {string} [opts.fileType]    'image' / 'video' / 'audio'
 * @returns {Promise<string|null>} blob URL（命中时），或 null（未缓存）
 */
export async function resolveLocalOnly(url, opts = {}) {
  if (!url || url.startsWith('blob:') || url.startsWith('data:')) {
    return null;
  }

  const { crewId, crewName, fileType } = opts;
  // 仅当三个参数齐全 + 浏览器支持 + 用户已授权目录（cachedHandle 不为空）时启用 L2
  // 注意：直接读取 cachedHandle 模块变量，不调 restoreCacheFolder（避免触发权限提示）
  const canTryL2 = !!(crewId && crewName && fileType && isFileSystemAccessSupported() && cachedHandle);

  // ── local-cache:// URL：从 IndexedDB 读取原始文件 ──
  if (url.startsWith('local-cache://')) {
    const match = url.match(/^local-cache:\/\/([^/]+)\/([^/]+)\/(.+)$/);
    if (!match) return null;
    const [, savedCrewId, savedTypeFolder, fileName] = match;
    const cacheKey = 'file://' + fileName;
    // L0：内存缓存
    if (blobUrlCache.has(cacheKey)) {
      return blobUrlCache.get(cacheKey);
    }
    // L1：IndexedDB
    try {
      const blob = await getBlobFromDB(cacheKey);
      if (blob) return createCachedBlobUrl(cacheKey, blob);
    } catch (_) {}
    // L2：本地文件夹（local-cache:// URL 自带 crewId 和 typeFolder，无需 opts）
    if (isFileSystemAccessSupported() && cachedHandle) {
      try {
        const ft = savedTypeFolder === 'images' ? 'image'
                 : savedTypeFolder === 'videos' ? 'video'
                 : 'audio';
        const localBlob = await readFromLocalFolder(savedCrewId, crewName, ft, fileName);
        if (localBlob && localBlob.size > 0) {
          // 后台回填 IndexedDB（不阻塞）
          evictIfNeeded(localBlob.size)
            .then(() => putBlobToDB(cacheKey, localBlob, ft))
            .catch(() => {});
          return createCachedBlobUrl(cacheKey, localBlob);
        }
      } catch (_) {}
    }
    return null;
  }

  // ── OSS / 远程 URL：提取文件名 → 查缓存 ──
  const fileName = extractFileNameFromUrl(url);
  if (!fileName) return null;
  const cacheKey = 'file://' + fileName;

  // L0：内存缓存（同步命中，最快）
  if (blobUrlCache.has(cacheKey)) {
    return blobUrlCache.get(cacheKey);
  }

  // L1：IndexedDB（异步，但不涉及网络）
  try {
    const blob = await getBlobFromDB(cacheKey);
    if (blob) return createCachedBlobUrl(cacheKey, blob);
  } catch (_) {}

  // L2：本地文件夹（仅在调用方提供了 crew/fileType 且用户已授权时尝试）
  if (canTryL2) {
    try {
      const localBlob = await readFromLocalFolder(crewId, crewName, fileType, fileName);
      if (localBlob && localBlob.size > 0) {
        // 后台回填 IndexedDB（不阻塞首屏返回）
        evictIfNeeded(localBlob.size)
          .then(() => putBlobToDB(cacheKey, localBlob, fileType))
          .catch(() => {});
        return createCachedBlobUrl(cacheKey, localBlob);
      }
    } catch (_) {}
  }

  return null;
}

export function extractFileNameFromUrl(url) {
  if (!url) return null;
  try {
    const urlObj = new URL(url);
    const pathname = urlObj.pathname;
    const parts = pathname.split('/');
    return parts[parts.length - 1];
  } catch (e) {
    const parts = url.split('/');
    return parts[parts.length - 1];
  }
}

export function getFileTypeFromMime(mimeType) {
  if (!mimeType) return 'image';
  if (mimeType.startsWith('video/')) return 'video';
  if (mimeType.startsWith('audio/')) return 'audio';
  return 'image';
}

export function getFileTypeFromUrl(url) {
  if (!url) return 'image';
  const lower = url.toLowerCase();
  if (lower.match(/\.(mp4|webm|mov|avi|mkv)(\?|$)/)) return 'video';
  if (lower.match(/\.(mp3|wav|ogg|aac|m4a)(\?|$)/)) return 'audio';
  return 'image';
}

// ==================== 核心读写 API（双层） ====================

/**
 * 将文件写入缓存（双层：IndexedDB + 本地文件夹）
 */
export async function writeFileToCache(crewId, crewName, fileType, fileName, blob) {
  // 第一层：IndexedDB Blob 缓存（始终写入）
  try {
    const cacheKey = 'file://' + fileName;
    await evictIfNeeded(blob.size);
    await putBlobToDB(cacheKey, blob, fileType);
    console.log('📁 已缓存: ' + fileName + ' (' + (blob.size / 1024).toFixed(0) + 'KB)');
  } catch (e) {
    console.warn('IndexedDB 写入失败:', e);
  }

  // 第二层：本地文件夹（如果已配置，后台写入不阻塞）
  writeToLocalFolder(crewId, crewName, fileType, fileName, blob);

  return { success: true };
}

/**
 * 从缓存读取文件（IndexedDB 优先）
 */
export async function readFileFromCache(crewId, crewName, fileType, fileName) {
  // 先尝试 IndexedDB（更快）
  try {
    const cacheKey = 'file://' + fileName;
    const blob = await getBlobFromDB(cacheKey);
    if (blob) return blob;
  } catch (e) {
    console.warn('IndexedDB 读取失败:', e);
  }

  // 再尝试本地文件夹
  return await readFromLocalFolder(crewId, crewName, fileType, fileName);
}

/**
 * 检查文件是否存在于缓存中
 */
export async function isFileInCache(crewId, crewName, fileType, fileName) {
  const file = await readFileFromCache(crewId, crewName, fileType, fileName);
  return file !== null;
}

/**
 * � 将 IndexedDB 中已缓存的文件回填到本地文件夹
 * 用于重新配置缓存文件夹后，把已有的数据写入新文件夹
 * 跟 getLocalUrlOrFetch 不同，这个函数不走内存缓存捕获，直接从 IndexedDB 读取并写入本地文件夹
 */
export async function syncToLocalFolder(serverUrl, crewId, crewName, fileType) {
  if (!serverUrl || serverUrl.startsWith('blob:') || serverUrl.startsWith('data:')) return;
  if (!cachedHandle) return; // 没有配置本地文件夹则跳过

  const fileName = extractFileNameFromUrl(serverUrl);
  if (!fileName) return;

  const cacheKey = 'file://' + fileName;

  try {
    // 从 IndexedDB 读取 blob
    const blob = await getBlobFromDB(cacheKey);
    if (blob) {
      await writeToLocalFolder(crewId, crewName, fileType, fileName, blob);
      return;
    }

    // IndexedDB 中也没有，尝试从网络下载
    console.log('⬇️ 同步下载: ' + fileName);
    const response = await fetch(serverUrl);
    if (!response.ok) return;
    const downloaded = await response.blob();

    // 写入 IndexedDB + 本地文件夹
    evictIfNeeded(downloaded.size)
      .then(() => putBlobToDB(cacheKey, downloaded, fileType))
      .catch(err => console.warn('后台缓存写入失败:', err));
    await writeToLocalFolder(crewId, crewName, fileType, fileName, downloaded);
  } catch (e) {
    console.warn('同步到本地文件夹失败:', e.message);
  }
}

/**
 * �🌟 核心功能：获取本地缓存 URL，如果没有则从服务器下载并缓存
 */
/**
 * 📦 getLocalUrlOrFetch
 *
 * 统一资源获取入口，严格遵循如下优先级：
 *   1. 内存 blobUrl（极快）
 *   2. IndexedDB（快）
 *   3. 本地文件夹（快，需授权）
 *   4. 远程下载（慢，CORS/代理）
 *
 * 只要命中本地缓存，绝不走远程！
 *
 * @param {string} serverUrl 远程/本地/缓存 URL
 * @param {string} crewId
 * @param {string} crewName
 * @param {string} fileType
 * @returns {Promise<{url: string, isLocal: boolean}>}
 */
// 已迁移/废弃的存储域名 —— 这些域名的文件永久不可达，直接返回 null 避免前端反复重试刷屏
const DEAD_STORAGE_HOSTS = new Set([
  'r2-shucai.cineart.top',
  'r2-ref.cineart.top',
]);

export async function getLocalUrlOrFetch(serverUrl, crewId, crewName, fileType) {
  if (!serverUrl) {
    return { url: null, isLocal: false };
  }

  // 1. blob/data URL 直接返回
  if (serverUrl.startsWith('blob:') || serverUrl.startsWith('data:')) {
    return { url: serverUrl, isLocal: false };
  }

  // 1.5 废弃存储域名快速跳过 —— R2 已迁移到 OSS，这些 URL 域名解析永远失败
  //     先查本地缓存（IndexedDB/本地文件夹），命中则照常返回；缓存 miss 直接放弃，不发网络请求
  try {
    const deadHost = new URL(serverUrl).host;
    if (DEAD_STORAGE_HOSTS.has(deadHost)) {
      const fileName = extractFileNameFromUrl(serverUrl);
      if (fileName) {
        const cacheKey = 'file://' + fileName;
        if (blobUrlCache.has(cacheKey)) return { url: blobUrlCache.get(cacheKey), isLocal: true };
        const cachedBlob = await getBlobFromDB(cacheKey);
        if (cachedBlob) {
          const blobUrl = createCachedBlobUrl(cacheKey, cachedBlob);
          return { url: blobUrl, isLocal: true };
        }
        const localBlob = await readFromLocalFolder(crewId, crewName, fileType, fileName);
        if (localBlob) {
          const blobUrl = createCachedBlobUrl(cacheKey, localBlob);
          return { url: blobUrl, isLocal: true };
        }
      }
      // 本地也没有，文件不可恢复，静默跳过
      return { url: null, isLocal: false };
    }
  } catch (_) { /* URL 解析失败，继续正常流程 */ }


  // 2. local-cache:// URL（本地缓存直链）
  if (serverUrl.startsWith('local-cache://')) {
    const match = serverUrl.match(/^local-cache:\/\/([^/]+)\/([^/]+)\/(.+)$/);
    if (!match) {
      console.warn('无效的 local-cache URL:', serverUrl);
      return { url: null, isLocal: false };
    }
    const [, savedCrewId, savedFileType, fileName] = match;
    const ft = savedFileType === 'images' ? 'image' : savedFileType === 'videos' ? 'video' : 'audio';
    // 优先查 IndexedDB，再查本地文件夹
    const localBlob = await readFileFromCache(savedCrewId, crewName, ft, fileName);
    if (localBlob) {
      const cacheKey = 'file://' + fileName;
      const blobUrl = createCachedBlobUrl(cacheKey, localBlob);
      return { url: blobUrl, isLocal: true };
    }
    console.warn('⚠️ 本地缓存文件未找到:', fileName);
    return { url: null, isLocal: false };
  }

  const fileName = extractFileNameFromUrl(serverUrl);
  if (!fileName) {
    return { url: serverUrl, isLocal: false };
  }
  const cacheKey = 'file://' + fileName;

  // 3. 内存 blobUrl
  if (blobUrlCache.has(cacheKey)) {
    return { url: blobUrlCache.get(cacheKey), isLocal: true };
  }

  try {
    // 4. IndexedDB
    const cachedBlob = await getBlobFromDB(cacheKey);
    if (cachedBlob) {
      const blobUrl = createCachedBlobUrl(cacheKey, cachedBlob);
      // 后台回填本地文件夹（不阻塞主流程）
      writeToLocalFolder(crewId, crewName, fileType, fileName, cachedBlob);
      return { url: blobUrl, isLocal: true };
    }

    // 5. 本地文件夹
    const localBlob = await readFromLocalFolder(crewId, crewName, fileType, fileName);
    if (localBlob) {
      // 回填 IndexedDB（不阻塞主流程）
      evictIfNeeded(localBlob.size)
        .then(() => putBlobToDB(cacheKey, localBlob, fileType))
        .catch(err => console.warn('回填 IndexedDB 失败:', err));
      const blobUrl = createCachedBlobUrl(cacheKey, localBlob);
      return { url: blobUrl, isLocal: true };
    }

    // 6. 远程下载（全部 miss 才���！）— 使用 ReadableStream 追踪真实进度
    console.log('⬇️ 下载并缓存: ' + fileName);
    let blob;

    // ⚠️ 同源 vs 跨域用不同的阈值 —— 两者的网络路径完全不同：
    //   · 同源（教育版 MinIO 走 nginx 反代：https://cineart.dlpu.edu.cn/cineart-media/xxx）
    //     请求 → 学校服务器 → MinIO（127.0.0.1:9000），只在校园网内跑。
    //     500ms 首字节阈值在这里纯属误伤：服务器稍有负载就超时。
    //   · 跨域（主站 R2 / OSS，公网）
    //     直连延迟本来就低，500ms 够用，超时了正好早点切代理。
    //
    // ⚠️ 同源阈值为什么给到 15s：浏览器对同源最多 6 个并发连接（HTTP/1.1），
    //    而预加载器/idle 预热会一次发起 10+ 个下载请求。排在第 7 个之后的
    //    要**排队等空闲连接**才发得出去 —— 这不是服务器慢，是排队，
    //    3s 阈值必然误伤。实测重试立刻就通，证明连接一释放即可。
    //    跨域不受同源连接数限制（每个域独立 6 条），且需要 CORS 预检，
    //    保持原来的 500ms/2000ms 不变 —— 主站 R2/OSS 行为零影响。
    const sameOrigin = isSameOrigin(serverUrl);
    const TIMEOUT_MS = sameOrigin ? 30000 : 2000;           // 响应头超时
    const FIRST_BYTE_TIMEOUT_MS = sameOrigin ? 15000 : 500;  // 首字节超时

    try {
      // 使用带超时的 fetch，避免在网络极慢或远程 OSS 响应缓慢时长时间阻塞
      // 🔧 优化：缩短超时时间 + 增加首字节超时检测
      const controller = new AbortController();

      let firstByteReceived = false;

      // 主超时：到点强制 abort
      const mainTimeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS);

      // 首字节超时：窗口内没收到响应头则 abort
      const firstByteTimeoutId = setTimeout(() => {
        if (!firstByteReceived) {
          console.warn(`⏱️ 首字节超时 (${FIRST_BYTE_TIMEOUT_MS}ms)，切换到代理下载:`, fileName);
          controller.abort();
        }
      }, FIRST_BYTE_TIMEOUT_MS);

      let response;
      try {
        response = await fetch(serverUrl, { signal: controller.signal });
        firstByteReceived = true; // 收到响应头即视为首字节到达
        clearTimeout(firstByteTimeoutId);
      } catch (err) {
        // 中断/超时/网络错误都会走到这里，统一作为 directErr 处理
        clearTimeout(firstByteTimeoutId);
        throw err;
      } finally {
        clearTimeout(mainTimeoutId);
        clearTimeout(firstByteTimeoutId);
      }

      if (!response.ok) {
        throw new Error('HTTP ' + response.status);
      }

      blob = await fetchBlobWithProgress(response, cacheKey);
    } catch (directErr) {
      // 🔧 同源 URL 绝不能走 /api/proxy-image：
      //    后端 upload.js 里用 new URL(url) 解析，相对路径没有 base 会抛异常，
      //    被 catch 成固定的 400「无效的 URL」；而且它的 SSRF 白名单只认
      //    aliyuncs.com 那些外部域名，同源路径永远过不了。
      //    同源本来就没有 CORS 问题，代理这一跳注定失败、纯属浪费一次请求。
      //    这里改成不带超时地重试一次直连 —— 500ms 首字节超时对同源大图太激进，
      //    服务器稍有负载就会误判，而重试直连通常就成功了。
      //
      // ⚠️ 关键：判断「是否同源」不能只看「是不是绝对 URL」。
      //    教育版 MinIO 的 publicDomain 配成 https://cineart.dlpu.edu.cn/cineart-media，
      //    这是**绝对 URL 但同源**！早先只判断 /^https?:\/\// 会把它误判成跨域，
      //    于是每次都白等 500ms 超时 → abort → 再绕道代理 → 服务器把同一个文件
      //    再拉一遍。图能显示（代理最终成功），但日志里全是「首字节超时」，
      //    教育网慢速下尤为刺眼。必须按 origin 真正比较。
      if (isSameOrigin(serverUrl)) {
        console.warn('⚠️ 同源直连超时，跳过代理直接重试:', directErr && directErr.message);
        // 重试也不设无限期挂起 —— 服务器真的不可用时要有退出路径，
        // 否则这个 Promise 永远不 resolve，节点会一直转圈。
        const retryCtrl = new AbortController();
        const retryTimeoutId = setTimeout(() => retryCtrl.abort(), TIMEOUT_MS);
        let retryResp;
        try {
          retryResp = await fetch(serverUrl, { signal: retryCtrl.signal });
        } finally {
          clearTimeout(retryTimeoutId);
        }
        if (!retryResp.ok) {
          throw new Error('HTTP ' + retryResp.status);
        }
        blob = await fetchBlobWithProgress(retryResp, cacheKey);
      } else {
        // 跨域（OSS/R2 等外部域名）才需要服务端代理绕 CORS
        console.warn('⚠️ 直接下载失败或超时，尝试使用服务端代理:', directErr && directErr.message);
        try {
          const token = _getAuthToken();
          const proxyUrl = `${API_BASE_URL}/api/proxy-image?url=${encodeURIComponent(serverUrl)}`;
          const proxyResp = await fetch(proxyUrl, {
            headers: token ? { 'Authorization': `Bearer ${token}` } : {}
          });
          if (!proxyResp.ok) {
            throw new Error('代理下载也失败: HTTP ' + proxyResp.status);
          }
          blob = await fetchBlobWithProgress(proxyResp, cacheKey);
        } catch (proxyErr) {
          // 如果代理也失败，则向上抛出以触发最终回退
          throw proxyErr;
        }
      }
    }

    // 🛡️ 护栏 2：写缓存前再次校验（fetchBlobWithProgress 已抛 0 字节错误，
    // 这里是防护代理路径或未来变更导致绕过完整性校验）
    if (!blob || blob.size === 0) {
      console.warn('⚠️ 拒绝写入 0 字节缓存:', fileName, '— 原 URL 直接返回，不污染缓存');
      return { url: serverUrl, isLocal: false };
    }

    // 标记下载完成，3 秒后清理进度记录
    downloadProgressStore.set(cacheKey, { downloaded: blob.size, total: blob.size, status: 'done' });
    setTimeout(() => downloadProgressStore.delete(cacheKey), 3000);

    // 后台写入 IndexedDB
    evictIfNeeded(blob.size)
      .then(() => putBlobToDB(cacheKey, blob, fileType))
      .catch(err => console.warn('后台缓存写入失败:', err));

    // 后台写入本地文件夹
    writeToLocalFolder(crewId, crewName, fileType, fileName, blob);

    const blobUrl = createCachedBlobUrl(cacheKey, blob);
    return { url: blobUrl, isLocal: true };

  } catch (e) {
    // 标记下载失败
    if (cacheKey) {
      downloadProgressStore.set(cacheKey, { downloaded: 0, total: 0, status: 'error' });
      setTimeout(() => downloadProgressStore.delete(cacheKey), 5000);
    }
    console.warn('缓存处理失败，使用原始 URL:', e.message);
    return { url: serverUrl, isLocal: false };
  }
}

// ==================== 本地文件夹读写（原版完整恢复） ====================

/**
 * 将文件写入本地文件夹（第二层缓存）
 */
async function writeToLocalFolder(crewId, crewName, fileType, fileName, blob) {
  if (!cachedHandle) {
    const restored = await restoreCacheFolder();
    if (!restored.success) return;
  }

  try {
    const safeName = (crewName || '默认项目').replace(/[<>:"/\\|?*]/g, '_');
    const crewFolderName = safeName + '_' + crewId;
    const crewFolder = await getOrCreateSubfolder(cachedHandle, crewFolderName);

    const typeFolder = fileType === 'image' ? 'images'
                     : fileType === 'video' ? 'videos'
                     : 'audios';
    const subFolder = await getOrCreateSubfolder(crewFolder, typeFolder);

    // 🔍 检查文件是否已存在，避免重复写入
    try {
      const existingHandle = await subFolder.getFileHandle(fileName, { create: false });
      const existingFile = await existingHandle.getFile();

      // 如果文件已存在且大小一致，跳过写入
      if (existingFile.size === blob.size) {
        return;
      }
    } catch (e) {
      // NotFoundError 表示文件不存在，继续写入
      if (e.name !== 'NotFoundError') {
        throw e;
      }
    }

    const fileHandle = await subFolder.getFileHandle(fileName, { create: true });
    const writable = await fileHandle.createWritable();
    await writable.write(blob);
    await writable.close();

    console.log('📂 已写入本地: ' + crewFolderName + '/' + typeFolder + '/' + fileName);
  } catch (e) {
    // 静默失败，不影响主流程
    if (e.name !== 'NotAllowedError' && e.name !== 'SecurityError') {
      console.warn('写入本地文件夹失败（不影响使用）:', e.message);
    }
  }
}

/**
 * 从本地文件夹读取文件
 */
async function readFromLocalFolder(crewId, crewName, fileType, fileName) {
  if (!cachedHandle) {
    const restored = await restoreCacheFolder();
    if (!restored.success) return null;
  }

  try {
    const safeName = (crewName || '默认项目').replace(/[<>:"/\\|?*]/g, '_');
    const crewFolderName = safeName + '_' + crewId;

    const crewFolder = await cachedHandle.getDirectoryHandle(crewFolderName);
    const typeFolder = fileType === 'image' ? 'images'
                     : fileType === 'video' ? 'videos'
                     : 'audios';
    const subFolder = await crewFolder.getDirectoryHandle(typeFolder);
    const fileHandle = await subFolder.getFileHandle(fileName);
    const file = await fileHandle.getFile();

    console.log('📂 从本地缓存读取: ' + crewFolderName + '/' + typeFolder + '/' + fileName);
    return file;
  } catch (e) {
    // NotFoundError 是正常的（文件不在缓存中）
    return null;
  }
}

/**
 * 项目改名时迁移缓存文件夹（原版完整逻辑）
 */
export async function migrateCacheFolder(crewId, oldName, newName) {
  if (!cachedHandle) {
    const restored = await restoreCacheFolder();
    if (!restored.success) {
      return { success: false, error: '本地缓存未配置' };
    }
  }

  const safeOldName = (oldName || '默认项目').replace(/[<>:"/\\|?*]/g, '_');
  const safeNewName = (newName || '默认项目').replace(/[<>:"/\\|?*]/g, '_');
  const oldFolderName = safeOldName + '_' + crewId;
  const newFolderName = safeNewName + '_' + crewId;

  if (oldFolderName === newFolderName) {
    return { success: true };
  }

  try {
    let oldFolder;
    try {
      oldFolder = await cachedHandle.getDirectoryHandle(oldFolderName);
    } catch (e) {
      console.log('📁 旧缓存文件夹 ' + oldFolderName + ' 不存在，无需迁移');
      return { success: true };
    }

    const newFolder = await cachedHandle.getDirectoryHandle(newFolderName, { create: true });

    const subFolders = ['images', 'videos', 'audios'];
    for (const subName of subFolders) {
      try {
        const oldSubFolder = await oldFolder.getDirectoryHandle(subName);
        const newSubFolder = await newFolder.getDirectoryHandle(subName, { create: true });

        for await (const entry of oldSubFolder.values()) {
          if (entry.kind === 'file') {
            const file = await entry.getFile();
            const newFileHandle = await newSubFolder.getFileHandle(entry.name, { create: true });
            const writable = await newFileHandle.createWritable();
            await writable.write(file);
            await writable.close();
          }
        }
      } catch (e) {
        if (e.name !== 'NotFoundError') {
          console.warn('复制子文件夹 ' + subName + ' 失败:', e);
        }
      }
    }

    await cachedHandle.removeEntry(oldFolderName, { recursive: true });

    console.log('✅ 缓存文件夹已迁移: ' + oldFolderName + ' → ' + newFolderName);
    return { success: true };

  } catch (e) {
    console.error('迁移缓存文件夹失败:', e);
    return { success: false, error: e.message };
  }
}

/**
 * 验证缓存配置是否有效
 * 返回值：
 *   { valid: true, path }                          — 配置正常
 *   { valid: false, notConfigured: true }           — 从未配置过（新用户）
 *   { valid: false, needReselect: true, path? }     — 配置存在但不可用（换电脑/文件夹删除/权限丢失）
 */
export async function validateCacheConfig() {
  if (!isFileSystemAccessSupported()) {
    return { valid: false, needReselect: false };
  }

  try {
    const handle = await getFromFsDB(getUserKey('rootFolderHandle'));
    const path = await getFromFsDB(getUserKey('rootFolderPath'));

    if (!handle) {
      // 从未配置过本地缓存
      return { valid: false, notConfigured: true, needReselect: false };
    }

    const permission = await handle.queryPermission({ mode: 'readwrite' });

    if (permission === 'granted') {
      try {
        const iter = handle.values();
        await iter.next();
      } catch (accessError) {
        console.warn('缓存文件夹已被删除:', accessError);
        return { valid: false, needReselect: true };
      }

      cachedHandle = handle;
      cachedPath = path;
      return { valid: true, path };
    }

    console.log('缓存权限状态:', permission);
    return { valid: false, path, needReauth: true };

  } catch (e) {
    console.error('验证缓存配置失败:', e);
    return { valid: false, needReselect: true };
  }
}

// ==================== 缓存管理工具 ====================

export async function getCacheInfo() {
  try {
    const { totalSize, entries } = await getCacheStats();
    return {
      totalEntries: entries.length,
      totalSizeMB: (totalSize / 1024 / 1024).toFixed(1),
      maxSizeMB: (MAX_CACHE_BYTES / 1024 / 1024).toFixed(0),
      usagePercent: ((totalSize / MAX_CACHE_BYTES) * 100).toFixed(1)
    };
  } catch (e) {
    return { error: e.message };
  }
}

export async function clearAllCache() {
  try {
    clearBlobUrlCache();
    const db = await openBlobDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction([BLOB_STORE, META_STORE], 'readwrite');
      tx.objectStore(BLOB_STORE).clear();
      tx.objectStore(META_STORE).clear();
      tx.oncomplete = () => {
        console.log('🗑️ IndexedDB 缓存已清空');
        resolve({ success: true });
      };
      tx.onerror = () => reject(tx.error);
    });
  } catch (e) {
    return { success: false, error: e.message };
  }
}

// ==================== 本地文件夹删除 API ====================

/**
 * 从本地缓存文件夹中删除单个文件
 * 仅删除第二层（File System Access API 本地文件夹），不影响 IndexedDB 缓存
 * @param {number} crewId - 项目 ID
 * @param {string} crewName - 项目名称
 * @param {string} fileType - 文件类型: 'image' | 'video' | 'audio'
 * @param {string} fileName - 文件名（从 URL 中提取）
 */
export async function deleteFileFromCache(crewId, crewName, fileType, fileName) {
  if (!fileName) return;

  // 第一层：删除 IndexedDB 缓存
  try {
    const cacheKey = 'file://' + fileName;
    await deleteBlobFromDB(cacheKey);
    // 同时清理内存 blob URL 缓存
    if (blobUrlCache.has(cacheKey)) {
      URL.revokeObjectURL(blobUrlCache.get(cacheKey));
      blobUrlCache.delete(cacheKey);
    }
  } catch (e) {
    console.warn('IndexedDB 删除失败:', e);
  }

  // 第二层：删除本地文件夹缓存
  if (!cachedHandle) return;
  try {
    const safeName = (crewName || '默认项目').replace(/[<>:"/\\|?*]/g, '_');
    const crewFolderName = safeName + '_' + crewId;

    const crewFolder = await cachedHandle.getDirectoryHandle(crewFolderName);
    const typeFolder = fileType === 'image' ? 'images'
                     : fileType === 'video' ? 'videos'
                     : 'audios';
    const subFolder = await crewFolder.getDirectoryHandle(typeFolder);
    await subFolder.removeEntry(fileName);

    console.log('🗑️ 已删除本地缓存: ' + crewFolderName + '/' + typeFolder + '/' + fileName);

    // 🔧 清理空文件夹：如果子文件夹已空，尝试删除子文件夹和项目文件夹
    try {
      const subEntries = subFolder.values();
      const firstEntry = await subEntries.next();
      if (firstEntry.done) {
        // 子文件夹已空，删除它
        await crewFolder.removeEntry(typeFolder);
        console.log('🗑️ 已删除空文件夹: ' + crewFolderName + '/' + typeFolder);

        // 检查项目文件夹是否也空了
        const crewEntries = crewFolder.values();
        const firstCrewEntry = await crewEntries.next();
        if (firstCrewEntry.done) {
          await cachedHandle.removeEntry(crewFolderName);
          console.log('🗑️ 已删除空项目文件夹: ' + crewFolderName);
        }
      }
    } catch (cleanupErr) {
      // 清理空目录失败不影响主流程
    }
  } catch (e) {
    // NotFoundError 说明文件本来就不存在，静默处理
    if (e.name !== 'NotFoundError' && e.name !== 'NotAllowedError' && e.name !== 'SecurityError') {
      console.warn('删除本地缓存文件失败（不影响使用）:', e.message);
    }
  }
}

/**
 * 从本地缓存文件夹中删除一个 URL 对应的文件（自动提取 fileName 和 fileType）
 * @param {number} crewId - 项目 ID
 * @param {string} crewName - 项目名称
 * @param {string} url - 文件的完整 URL
 */
export async function deleteFileFromCacheByUrl(crewId, crewName, url) {
  if (!url) return;
  const fileName = extractFileNameFromUrl(url);
  const fileType = getFileTypeFromUrl(url);
  if (fileName) {
    await deleteFileFromCache(crewId, crewName, fileType, fileName);
  }
}

/**
 * 删除整个项目的本地缓存文件夹（用于删除项目时）
 * @param {number} crewId - 项目 ID
 * @param {string} crewName - 项目名称
 */
export async function deleteCrewCacheFolder(crewId, crewName) {
  if (!cachedHandle) return;

  try {
    const safeName = (crewName || '默认项目').replace(/[<>:"/\\|?*]/g, '_');
    const crewFolderName = safeName + '_' + crewId;
    await cachedHandle.removeEntry(crewFolderName, { recursive: true });
    console.log('🗑️ 已删除项目本地缓存文件夹: ' + crewFolderName);
  } catch (e) {
    if (e.name !== 'NotFoundError' && e.name !== 'NotAllowedError' && e.name !== 'SecurityError') {
      console.warn('删除项目缓存文件夹失败（不影响使用）:', e.message);
    }
  }
}

// ==================== 默认导出 ====================

/**
 * 🔒 内部方法：获取已授权的根文件夹 handle
 * 仅供 projectCacheService 等内部模块使用，不暴露给外部
 * 
 * @returns {Promise<FileSystemDirectoryHandle|null>}
 */
async function _getAuthorizedRootHandle() {
  // 如果已有缓存的 handle，直接返回
  if (cachedHandle) return cachedHandle;

  // 尝试从 IndexedDB 恢复 handle
  try {
    const handle = await getFromFsDB(getUserKey('rootFolderHandle'));
    if (!handle) return null;

    // 检查权限
    const permission = await handle.queryPermission({ mode: 'readwrite' });
    if (permission === 'granted') {
      cachedHandle = handle;
      return handle;
    }
  } catch (e) {
    // 恢复失败，返回 null
  }

  return null;
}

const localCacheService = {
  isFileSystemAccessSupported,
  hasCacheConfigured,
  getCachePath,
  requestCacheFolder,
  updateCachePath,
  restoreCacheFolder,
  reauthorizeCacheFolder,
  clearCacheConfig,
  writeFileToCache,
  readFileFromCache,
  isFileInCache,
  migrateCacheFolder,
  extractFileNameFromUrl,
  getFileTypeFromMime,
  getFileTypeFromUrl,
  getLocalUrlOrFetch,
  resolveLocalOnly,
  syncToLocalFolder,
  prepareBgRemovalAssets,
  warmBgRemovalAssets,
  restoreBgRemovalAssetsToBrowserCache,
  syncBgRemovalAssetsFromBrowserCache,
  prepareFfmpegAssets,
  restoreFfmpegAssetsToBrowserCache,
  syncFfmpegAssetsFromBrowserCache,
  cacheFfmpegAssetsFromRemote,
  validateCacheConfig,
  clearBlobUrlCache,
  pinBlobUrl,
  unpinBlobUrl,
  setCurrentUser,
  getCurrentUserId,
  getCacheInfo,
  clearAllCache,
  deleteFileFromCache,
  deleteFileFromCacheByUrl,
  deleteCrewCacheFolder,
  getDownloadProgressByUrl,
  _getAuthorizedRootHandle,  // 🔒 内部方法（projectCacheService 专用）
};

export default localCacheService;
